import { FC, useEffect, useMemo, useState } from "react"
import { DataGrid } from "@/core/components"
import {
  getGridStringOperators,
  GridActionsCellItem,
  GridCallbackDetails,
  gridClasses,
  GridColDef,
  GridFilterModel,
} from "@mui/x-data-grid-pro"
import { GRID_PAGE_SIZE_OPTIONS } from "@/core/constants"
import { useAppDispatch, useAppSelector } from "@/modules/app/hooks"
import {
  alpha,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Switch,
  Theme,
} from "@mui/material"
import {
  Edit,
  HideSource,
  LinkOff,
  SettingsBackupRestore,
} from "@mui/icons-material"
import { formatTGLink } from "@/modules/adv2/components/utils/string"
import {
  getTgstatBotState,
  setInviteLinksFilter,
  setInviteLinksPaginationModel,
  setShowHidden,
  useGetInviteLinksQuery,
  useHideInviteLinkMutation,
  useRevokeInviteLinkMutation,
} from "../store"
import { TgstatBot } from "../interfaces"
import { useParams } from "react-router-dom"
import { formatDateAndTime } from "@/core/utils"
import { EditInviteLinkForm } from "./editInviteLinkForm"

const styles = (theme: Theme) => ({
  [`&.MuiDataGrid-root .${gridClasses.row}--revoked`]: {
    backgroundColor: `${alpha(theme.palette.grey[700], 0.1)} !important`,
  },
})

interface IProps {
  canEdit: boolean
}

export const InviteLinksTable: FC<IProps> = ({ canEdit }) => {
  const { chatId, botId } = useParams()
  const dispatch = useAppDispatch()
  const {
    inviteLinks: { page, pageSize },
    showHidden,
    inviteLinksFilters,
  } = useAppSelector(getTgstatBotState)
  const { isFetching, data: dataAndTotal } = useGetInviteLinksQuery({
    queryParams: {
      chat_id: chatId!,
      bot_id: botId!,
      page: page + 1,
      page_size: pageSize,
      with_hidden: showHidden,
      filters: inviteLinksFilters,
    },
  })
  const [data, totalCount] = dataAndTotal ?? []
  const [revoke, { isLoading: isRevokeLoading }] = useRevokeInviteLinkMutation()
  const [hide, { isLoading: isHideInviteLinkLoading }] =
    useHideInviteLinkMutation()
  const [editingLink, setEditingLink] = useState<TgstatBot.InviteLink | null>(
    null,
  )

  const handleEditClick = (link: TgstatBot.InviteLink) => {
    setEditingLink(link)
  }

  const handleEditClose = () => {
    setEditingLink(null)
  }

  useEffect(() => {
    dispatch(setInviteLinksFilter())
    dispatch(
      setInviteLinksPaginationModel({
        page: 0,
        pageSize,
      }),
    )
  }, [])

  const columns: GridColDef<TgstatBot.InviteLink>[] = useMemo(
    () => [
      {
        headerName: "Дата создания",
        field: "created_at",
        flex: 1,
        filterable: false,
        valueFormatter: ({ value }) => formatDateAndTime(value),
      },
      {
        headerName: "Название",
        field: "name",
        flex: 2,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
          (o) => o?.value === "contains",
        ),
      },
      {
        headerName: "Комментарий",
        field: "comment",
        flex: 3,
        filterable: true,
        filterOperators: getGridStringOperators().filter(
          (o) => o?.value === "contains",
        ),
      },
      {
        headerName: "Присоединилось всего",
        field: "joined_count",
        flex: 1,
        filterable: false,
      },
      {
        headerName: "Отписалось всего",
        field: "left_count",
        flex: 1,
        filterable: false,
      },
      {
        headerName: "Ссылка",
        field: "invite_link",
        renderCell: ({ value }) => {
          const url = formatTGLink(value)

          return (
            <Link
              color="secondary"
              href={url}
              target="_blank"
              referrerPolicy="no-referrer"
            >
              {url}
            </Link>
          )
        },
        flex: 3,
        filterable: false,
      },
      {
        headerName: "Канал",
        field: "chat",
        valueGetter: ({ value }) => value.chatTitle,
        flex: 3,
        filterable: false,
      },
      {
        headerName: "Лимит вступлений",
        field: "member_limit",
        flex: 1,
        filterable: false,
      },
      {
        headerName: "Действительна до",
        field: "expire_date",
        flex: 1,
        filterable: false,
        valueFormatter: ({ value }) => formatDateAndTime(value),
      },
      {
        headerName: "По заявкам",
        field: "creates_join_request",
        flex: 1,
        filterable: false,
        type: "boolean",
      },
      {
        headerName: "Отозвана",
        field: "is_revoked",
        flex: 1,
        filterable: false,
        type: "boolean",
      },
    ],
    [],
  )

  const handleFilterModeChange = (
    model: GridFilterModel,
    details: GridCallbackDetails<"filter">,
  ) => {
    if (!details.reason) {
      return
    }
    dispatch(
      setInviteLinksPaginationModel({
        page: 0,
        pageSize,
      }),
    )
    const filter = model.items.reduce<{ [key: string]: string }>(
      (filter, item) => {
        filter[item.field] = item.value
        return filter
      },
      {},
    )

    dispatch(setInviteLinksFilter(filter))
  }

  return (
    <>
      <Dialog
        open={!!editingLink}
        onClose={handleEditClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Редактировать ссылку</DialogTitle>
        <DialogContent>
          {editingLink && (
            <EditInviteLinkForm
              inviteLink={editingLink}
              onSuccess={handleEditClose}
            />
          )}
        </DialogContent>
      </Dialog>
      <FormControlLabel
        control={
          <Switch
            // value={showHidden}
            checked={showHidden}
            onChange={(e, value) => dispatch(setShowHidden(value))}
            inputProps={{ "aria-label": "Показать скрытые" }}
          />
        }
        label="Показать скрытые"
        labelPlacement="start"
      />
      <DataGrid<TgstatBot.InviteLink>
        columns={columns}
        rows={data ?? []}
        getRowId={(row) => row.invite_link}
        rowCount={totalCount ?? 0}
        loading={isFetching || isRevokeLoading || isHideInviteLinkLoading}
        pagination
        paginationMode="server"
        pageSizeOptions={GRID_PAGE_SIZE_OPTIONS}
        onPaginationModelChange={(model) =>
          dispatch(setInviteLinksPaginationModel(model))
        }
        filterMode="server"
        unstable_headerFilters
        filterDebounceMs={700}
        onFilterModelChange={handleFilterModeChange}
        sx={styles}
        initialState={{
          pagination: { paginationModel: { page, pageSize } },
          columns: {
            columnVisibilityModel: {
              chat: chatId === undefined,
            },
          },
        }}
        getRowClassName={(params) =>
          params.row.is_hidden
            ? `${gridClasses.row}--hidden`
            : params.row.is_revoked
              ? `${gridClasses.row}--revoked`
              : ""
        }
        rowActions={[
          (params) => (
            <GridActionsCellItem
              label="Редактировать"
              icon={<Edit />}
              showInMenu
              disabled={params.row.is_revoked || !canEdit}
              onClick={() => handleEditClick(params.row)}
            />
          ),
          (params) => (
            <GridActionsCellItem
              label="Отозвать"
              icon={<LinkOff />}
              showInMenu
              disabled={params.row.is_revoked || !canEdit}
              onClick={() => {
                revoke({ pathParams: { invite_link: params.row.invite_link } })
              }}
            />
          ),
          (params) => (
            <GridActionsCellItem
              label={params.row.is_hidden ? "Отобразить" : "Скрыть"}
              icon={
                isHideInviteLinkLoading ? (
                  <CircularProgress size="small" />
                ) : params.row.is_hidden ? (
                  <SettingsBackupRestore />
                ) : (
                  <HideSource />
                )
              }
              showInMenu
              onClick={() => {
                hide({
                  pathParams: { invite_link: params.row.invite_link },
                  body: { is_hidden: !params.row.is_hidden },
                })
              }}
              disabled={isHideInviteLinkLoading}
            />
          ),
        ]}
      />
    </>
  )
}
